import { create } from "zustand";
import toast from "react-hot-toast";
import { creativesService } from "@/feature/ads/cretives/services/creatives.service";

export type GeneratingItemStatus = "generating" | "done" | "error";

export interface GeneratingItem {
  id: string;
  backendId?: number;
  status: GeneratingItemStatus;
  imageUrl?: string;
  format: string;
  productName: string;
  productImageUrl?: string;
  errorMessage?: string;
}

interface GenerateJobPayload {
  product_name: string;
  product_image: string;
  product_description: string;
  user_prompt: string;
  image_template: string;
  format: string;
  template_id: string;
  color_mode: "auto" | "custom";
  primary_color: string | null;
  background_color: string | null;
  reference_type: "competitor_ad" | "template";
  config: { language: string };
}

interface GenerateJobMeta {
  productId: string;
  websiteId: string;
}

interface CreativeGenerationState {
  items: GeneratingItem[];
  startGeneration: (
    jobs: GenerateJobPayload[],
    meta: GenerateJobMeta
  ) => Promise<boolean>;
  clearCompleted: () => void;
  clearAll: () => void;
  removeItems: (ids: string[]) => void;
}

let jobCounter = 0;
let pollingInterval: ReturnType<typeof setInterval> | null = null;

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 24; // 2 minutes max

function stopPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}

function startPolling() {
  if (pollingInterval) return;

  let attempts = 0;

  pollingInterval = setInterval(async () => {
    attempts++;
    const { items } = useCreativeGenerationStore.getState();

    // Check ALL generating items (with or without backendId)
    const allGenerating = items.filter((i) => i.status === "generating");
    const generatingWithId = allGenerating.filter((i) => i.backendId);

    // Stop only if nothing is generating at all
    if (allGenerating.length === 0) {
      stopPolling();
      return;
    }

    // Time out all stuck items after max attempts
    if (attempts > MAX_POLL_ATTEMPTS) {
      stopPolling();
      useCreativeGenerationStore.setState((state) => ({
        items: state.items.map((i) =>
          i.status === "generating"
            ? {
                ...i,
                status: "error" as const,
                errorMessage: "Tiempo de espera agotado",
              }
            : i
        ),
      }));
      return;
    }

    // Only call API if there are items with backendId to match
    if (generatingWithId.length === 0) return;

    try {
      // Fetch all creatives (no filter) and match by backend ID
      const creatives = await creativesService.listByProductId();

      useCreativeGenerationStore.setState((state) => ({
        items: state.items.map((item) => {
          if (item.status !== "generating" || !item.backendId) return item;

          const match = creatives.find((c) => c.id === item.backendId);
          if (match && match.image_url) {
            return {
              ...item,
              status: "done" as const,
              imageUrl: match.image_url,
            };
          }
          if (match && match.status === "FAILED") {
            return {
              ...item,
              status: "error" as const,
              errorMessage: "La generación del anuncio falló",
            };
          }
          return item;
        }),
      }));

      // Stop if all done
      const updated = useCreativeGenerationStore.getState().items;
      if (!updated.some((i) => i.status === "generating")) {
        stopPolling();
      }
    } catch (err) {
      console.error("[CreativeGeneration] Polling error:", err);
    }
  }, POLL_INTERVAL_MS);
}

const useCreativeGenerationStore = create<CreativeGenerationState>((set) => ({
  items: [],

  startGeneration: async (jobs, meta) => {
    const payload = {
      product_name: jobs[0].product_name,
      product_image: jobs[0].product_image,
      product_description: jobs[0].product_description,
      templates: jobs.map((job) => ({
        user_prompt: job.user_prompt,
        image_template: job.image_template,
        format: job.format,
        template_id: job.template_id,
        color_mode: job.color_mode,
        primary_color: job.primary_color,
        background_color: job.background_color,
        reference_type: job.reference_type,
        config: job.config,
      })),
    };

    try {
      console.log(
        "[CreativeGeneration] Calling async endpoint:",
        JSON.stringify(payload, null, 2)
      );

      const createdCreatives =
        await creativesService.generateCreativesAsync(meta.websiteId, payload);

      console.log(
        "[CreativeGeneration] Backend returned creatives:",
        createdCreatives
      );

      const newItems: GeneratingItem[] = createdCreatives.map(
        (creative, i) => ({
          id: `gen-${Date.now()}-${jobCounter++}`,
          backendId: creative.id ?? undefined,
          status: "generating" as const,
          format: jobs[i]?.format || creative.format || "1:1",
          productName: jobs[0].product_name,
          productImageUrl: jobs[0].product_image || undefined,
        })
      );

      // If backend returned no creatives or none have a valid id, create local placeholders
      const hasTrackableItems = newItems.some((item) => item.backendId != null);
      if (newItems.length === 0 || !hasTrackableItems) {
        const fallbackItems: GeneratingItem[] = jobs.map((job) => ({
          id: `gen-${Date.now()}-${jobCounter++}`,
          status: "generating" as const,
          format: job.format,
          productName: job.product_name,
          productImageUrl: job.product_image || undefined,
        }));
        set((state) => ({
          items: [...fallbackItems, ...state.items],
        }));
      } else {
        set((state) => ({
          items: [...newItems, ...state.items],
        }));
      }

      // Start polling to detect when images are ready
      startPolling();

      return true;
    } catch (error: unknown) {
      const err = error as {
        message?: string;
        response?: { data?: any; status?: number };
      };
      console.error("[CreativeGeneration] Error:", err?.message);
      console.error(
        "[CreativeGeneration] Error response:",
        err?.response?.data
      );

      const backendMessage =
        err?.response?.data?.message || err?.response?.data?.error;
      toast.error(
        backendMessage || "Error al iniciar la generación. Intenta de nuevo."
      );
      return false;
    }
  },

  clearCompleted: () => {
    set((state) => ({
      items: state.items.filter((i) => i.status !== "done"),
    }));
    const remaining = useCreativeGenerationStore.getState().items;
    if (!remaining.some((i) => i.status === "generating")) {
      stopPolling();
    }
  },

  clearAll: () => {
    set({ items: [] });
    stopPolling();
  },

  removeItems: (ids) => {
    set((state) => ({
      items: state.items.filter((i) => !ids.includes(i.id)),
    }));
  },
}));

export default useCreativeGenerationStore;
